import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface PointOfInterestState {
  id: string;
  created_by: string;
  last_updated_by: string;
  datetime_created: string;
  datetime_updated: string | null;
  name: { [key: string]: string };
  category: [string, string];
  description: { [key: string]: string };
  type: [string, string];
  availability: [string, string];
  availability_detail: {
    ALL: boolean;
    STD: null | {
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    };
    STW: null | Array<{
      day_of_week: number[];
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    }>;
    STM: null | Array<{
      day_of_month: number[];
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    }>;
    SLD: Array<{
      date: string;
      start_time: { hour: number; minute: number };
      end_time: { hour: number; minute: number };
    }>;
  };
  status: [string, string];
}

export interface DirectionState {
  point_of_interest: {
    id: string;
    name: { [key: string]: string };
    category: [string, string];
    type: [string, string];
  };
  directions: {
    local: null | Array<{
      room: { id: string; name: { [key: string]: string } };
      directions: { [key: string]: string };
    }>;
    global: null | { id: string; directions: { [key: string]: string } };
  };
}

export interface VicinityState {
  point_of_interest: {
    id: string;
    name: string;
    category: [string, string];
    type: [string, string];
  };
  directions: {
    local: null | Array<{
      room: { id: string; name: { [key: string]: string } };
      directions: { [key: string]: string };
    }>;
    global: null | { id: string; directions: { [key: string]: string } };
  };
}

export interface DirectionsState {
  points_of_interest: DirectionState[];
  vicinity: VicinityState[];
}

// Define a type for the slice state
export interface OrientationState {
  points_of_interest: {
    data: PointOfInterestState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  directions: {
    data: DirectionsState | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: OrientationState = {
  points_of_interest: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  directions: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get points of interest
export const getPointsOfInterest = createAsyncThunk(
  "orientation/points-of-interest/get",
  async (
    obj: {
      category: string | null;
      type: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/orientation/point-of-interest/`;
      if (obj.category) url += `?category=${obj.category}`;
      if (obj.type) url += `${obj.category ? "&" : "?"}type=${obj.type}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create point of interest
export const createPointOfInterest = createAsyncThunk(
  "orientation/point-of-interest/post",
  async (
    obj: {
      name: { [key: string]: string };
      property: string;
      category: string;
      description: { [key: string]: string };
      type: string;
      availability: string;
      availabilityDetail: {
        ALL: boolean;
        STW: null | Array<{
          day_of_week: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        STM: null | Array<{
          day_of_month: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        name: obj.name,
        property: obj.property,
        category: obj.category,
        description: obj.description,
        type: obj.type,
        availability: obj.availability,
        availability_detail: obj.availabilityDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/orientation/point-of-interest/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update point of interest
export const updatePointOfInterest = createAsyncThunk(
  "orientation/point-of-interest/put",
  async (
    obj: {
      poiId: string;
      name: { [key: string]: string };
      property: string;
      category: string;
      description: { [key: string]: string };
      type: string;
      availability: string;
      availabilityDetail: {
        ALL: boolean;
        STW: null | Array<{
          day_of_week: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
        STM: null | Array<{
          day_of_month: number[];
          start_time: { hour: number; minute: number };
          end_time: { hour: number; minute: number };
        }>;
      };
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.poiId,
        name: obj.name,
        property: obj.property,
        category: obj.category,
        description: obj.description,
        type: obj.type,
        availability: obj.availability,
        availability_detail: obj.availabilityDetail,
        status: obj.status,
      };

      // Build URL
      const url = "/api/orientation/point-of-interest/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete points of interest
export const deletePointsOfInterest = createAsyncThunk(
  "orientation/points-of-interest/delete",
  async (
    obj: {
      poiIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.poiIds },
      };

      const res = await axios.delete(
        "/api/orientation/point-of-interest/",
        config,
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get directions for POIs
export const getDirections = createAsyncThunk(
  "orientation/directions/get",
  async (
    obj: {
      category: string | null;
      type: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/orientation/direction/`;
      if (obj.category) url += `?category=${obj.category}`;
      if (obj.type) url += `${obj.category ? "&" : "?"}type=${obj.type}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create global directions to point of interest
export const createGlobalDirectionsToPoi = createAsyncThunk(
  "orientation/point-of-interest/directions/global/post",
  async (
    obj: {
      property: string;
      point_of_interest: string;
      directions: { [key: string]: string };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        property: obj.property,
        point_of_interest: obj.point_of_interest,
        directions: obj.directions,
        directions_from: "A",
        directions_from_room: null,
      };

      // Build URL
      const url = "/api/orientation/direction/global/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update global directions to point of interest
export const updateGlobalDirectionsToPoi = createAsyncThunk(
  "orientation/point-of-interest/directions/global/put",
  async (
    obj: {
      property: string;
      point_of_interest: string;
      directionsId: string;
      directions: { [key: string]: string };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.directionsId,
        property: obj.property,
        point_of_interest: obj.point_of_interest,
        directions: obj.directions,
        directions_from: "A",
        directions_from_room: null,
      };

      // Build URL
      const url = "/api/orientation/direction/global/";

      // Send request
      const res = await axios.put(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create or update local directions to point of interest
export const createUpdateLocalDirectionsToPoi = createAsyncThunk(
  "orientation/point-of-interest/directions/local/post",
  async (
    obj: {
      property: string;
      poiId: string;
      directions: Array<{
        room_id: string;
        room_name: string;
        directions: { [key: string]: string };
      }>;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.poiId,
        property: obj.property,
        data: obj.directions,
      };

      // Build URL
      const url = "/api/orientation/direction/local/point-of-interest/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get directions for things in the local vicinity to a room
export const getVicinity = createAsyncThunk(
  "orientation/vicinity/get",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/orientation/direction/local/`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create or update local directions to point of interest
export const createUpdateLocalDirectionsToVicinity = createAsyncThunk(
  "orientation/vicinity/directions/local/post",
  async (
    obj: {
      property: string;
      vicinityId: string;
      directions: Array<{
        room_id: string;
        room_name: string;
        directions: { [key: string]: string };
      }>;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.vicinityId,
        property: obj.property,
        data: obj.directions,
      };

      // Build URL
      const url = "/api/orientation/direction/local/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const orientationSlice = createSlice({
  name: "orientation",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get points of interest
      .addCase(getPointsOfInterest.pending, (state) => {
        state.points_of_interest.loading = true;
        state.points_of_interest.waiting = false;
      })
      .addCase(getPointsOfInterest.fulfilled, (state, action) => {
        state.points_of_interest.data = action.payload.data.data;
        state.options.category = action.payload.data.options.category;
        state.options.type = action.payload.data.options.type;
        state.options.availability = action.payload.data.options.availability;
        state.options.status = action.payload.data.options.status;
        state.points_of_interest.loading = false;
        state.points_of_interest.waiting = false;
      })
      .addCase(getPointsOfInterest.rejected, (state) => {
        state.points_of_interest.loading = false;
        state.points_of_interest.waiting = false;
      })

      // Create point of interest
      .addCase(createPointOfInterest.pending, (state) => {
        state.points_of_interest.loading = true;
        state.points_of_interest.waiting = false;
      })
      .addCase(createPointOfInterest.fulfilled, (state, action) => {
        state.points_of_interest.data =
          state.points_of_interest.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.points_of_interest.data];
        state.points_of_interest.loading = false;
        state.points_of_interest.waiting = false;
      })
      .addCase(createPointOfInterest.rejected, (state) => {
        state.points_of_interest.loading = false;
        state.points_of_interest.waiting = false;
      })

      // Update point of interest
      .addCase(updatePointOfInterest.pending, (state) => {
        state.points_of_interest.waiting = true;
        state.points_of_interest.loading = true;
      })
      .addCase(updatePointOfInterest.fulfilled, (state, action) => {
        const previousData =
          state.points_of_interest.data === null
            ? []
            : state.points_of_interest.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const event = previousData[i];
          if (event.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(event);
          }
        }
        state.points_of_interest.data = newData;
        state.points_of_interest.loading = false;
        state.points_of_interest.waiting = false;
      })
      .addCase(updatePointOfInterest.rejected, (state) => {
        state.points_of_interest.waiting = false;
        state.points_of_interest.loading = false;
      })

      // Delete points of interest
      .addCase(deletePointsOfInterest.pending, (state) => {
        state.points_of_interest.loading = true;
        state.points_of_interest.waiting = false;
      })
      .addCase(deletePointsOfInterest.fulfilled, (state, action) => {
        const existingData =
          state.points_of_interest.data === null
            ? []
            : state.points_of_interest.data;
        const updatedData = existingData.filter(
          (poi) => !action.payload.data.id.includes(poi.id),
        );
        state.points_of_interest.data = updatedData;
        state.points_of_interest.loading = false;
        state.points_of_interest.waiting = false;
      })
      .addCase(deletePointsOfInterest.rejected, (state) => {
        state.points_of_interest.loading = false;
        state.points_of_interest.waiting = false;
      })

      // Get directions for POIs
      .addCase(getDirections.pending, (state) => {
        state.directions.loading = true;
        state.directions.waiting = false;
      })
      .addCase(getDirections.fulfilled, (state, action) => {
        state.directions.data = {
          points_of_interest: action.payload.data.data.points_of_interest,
          vicinity: action.payload.data.data.vicinity,
        };
        state.options.type = action.payload.data.options.type;
        state.options.poi_global =
          action.payload.data.options.point_of_interest.global;
        state.options.poi_local =
          action.payload.data.options.point_of_interest.local;
        state.directions.loading = false;
        state.directions.waiting = false;
      })
      .addCase(getDirections.rejected, (state) => {
        state.directions.loading = false;
        state.directions.waiting = false;
      })

      // Create global directions to POI
      .addCase(createGlobalDirectionsToPoi.pending, (state) => {
        state.directions.waiting = true;
        state.directions.loading = true;
      })
      .addCase(createGlobalDirectionsToPoi.fulfilled, (state, action) => {
        const previousData =
          state.directions.data === null ? null : state.directions.data;
        if (previousData === null) return;
        const newData = [];
        for (let i = 0; i < previousData.points_of_interest.length; i++) {
          const obj = previousData.points_of_interest[i];
          if (
            obj.point_of_interest.id ===
            action.payload.data.point_of_interest.id
          ) {
            newData.push(action.payload.data);
          } else {
            newData.push(obj);
          }
        }
        state.directions.data = {
          points_of_interest: newData,
          vicinity: previousData.vicinity,
        };
        state.directions.loading = false;
        state.directions.waiting = false;
      })
      .addCase(createGlobalDirectionsToPoi.rejected, (state) => {
        state.directions.waiting = false;
        state.directions.loading = false;
      })

      // Update global directions to POI
      .addCase(updateGlobalDirectionsToPoi.pending, (state) => {
        state.directions.waiting = true;
        state.directions.loading = true;
      })
      .addCase(updateGlobalDirectionsToPoi.fulfilled, (state, action) => {
        const previousData =
          state.directions.data === null ? null : state.directions.data;
        if (previousData === null) return;
        const newData = [];
        for (let i = 0; i < previousData.points_of_interest.length; i++) {
          const obj = previousData.points_of_interest[i];
          if (
            obj.point_of_interest.id ===
            action.payload.data.point_of_interest.id
          ) {
            newData.push(action.payload.data);
          } else {
            newData.push(obj);
          }
        }
        state.directions.data = {
          points_of_interest: newData,
          vicinity: previousData.vicinity,
        };
        state.directions.loading = false;
        state.directions.waiting = false;
      })
      .addCase(updateGlobalDirectionsToPoi.rejected, (state) => {
        state.directions.waiting = false;
        state.directions.loading = false;
      })

      // Create or update local directions to POI
      .addCase(createUpdateLocalDirectionsToPoi.pending, (state) => {
        state.directions.waiting = true;
        state.directions.loading = true;
      })
      .addCase(createUpdateLocalDirectionsToPoi.fulfilled, (state, action) => {
        const previousData =
          state.directions.data === null ? null : state.directions.data;
        if (previousData === null) return;
        const newData = [];
        for (let i = 0; i < previousData.points_of_interest.length; i++) {
          const obj = previousData.points_of_interest[i];
          if (
            obj.point_of_interest.id ===
            action.payload.data.point_of_interest.id
          ) {
            newData.push(action.payload.data);
          } else {
            newData.push(obj);
          }
        }
        state.directions.data = {
          points_of_interest: newData,
          vicinity: previousData.vicinity,
        };
        state.directions.loading = false;
        state.directions.waiting = false;
      })
      .addCase(createUpdateLocalDirectionsToPoi.rejected, (state) => {
        state.directions.waiting = false;
        state.directions.loading = false;
      })

      // Get vicinity
      .addCase(getVicinity.pending, (state) => {
        state.directions.loading = true;
        state.directions.waiting = false;
      })
      .addCase(getVicinity.fulfilled, (state, action) => {
        const newState =
          state.directions.data === null
            ? { vicinity: [], points_of_interest: [] }
            : state.directions.data;
        newState.vicinity = action.payload.data.data.vicinity;
        state.directions.data = newState;
        state.directions.loading = false;
        state.directions.waiting = false;
      })
      .addCase(getVicinity.rejected, (state) => {
        state.directions.loading = false;
        state.directions.waiting = false;
      })

      // Create or update local directions to vicinity
      .addCase(createUpdateLocalDirectionsToVicinity.pending, (state) => {
        state.directions.waiting = true;
        state.directions.loading = true;
      })
      .addCase(
        createUpdateLocalDirectionsToVicinity.fulfilled,
        (state, action) => {
          const previousData =
            state.directions.data === null ? null : state.directions.data;
          if (previousData === null) return;
          const newData = [];
          for (let i = 0; i < previousData.vicinity.length; i++) {
            const obj = previousData.vicinity[i];
            if (
              obj.point_of_interest.id ===
              action.payload.data.point_of_interest.id
            ) {
              newData.push(action.payload.data);
            } else {
              newData.push(obj);
            }
          }
          state.directions.data = {
            points_of_interest: previousData.points_of_interest,
            vicinity: newData,
          };
          state.directions.loading = false;
          state.directions.waiting = false;
        },
      )
      .addCase(createUpdateLocalDirectionsToVicinity.rejected, (state) => {
        state.directions.waiting = false;
        state.directions.loading = false;
      });
  },
});

export default orientationSlice.reducer;
